// Recrutement & onboarding — port complet de design/recrutement
// (Recrutement v13.html, rec12-shell/rec12-dash/rec13-queue/
// rec2-pipeline/rec5-panel/rec8-offres/rec9-onboard).
//
// Phase 1: catalogue de rôles seedé, demandes avec chaîne directeur →
// owner, file de validation. Phase 2: pipeline candidats, espace
// candidat, vivier, emails. Phase 3: appel scripté, entretiens,
// scorecards à révélation séquentielle. Phase 4: offres versionnées +
// PDF. Phase 5: onboarding (dossier admin, plan J1→J90, ressources,
// handoff → Employee) et tableau de bord rec12 complet.
//
// Deviations du proto (délibérées):
// - topbar/persona switcher/entity pills/Tweaks du proto → app shell +
//   EntityBar + vraies permissions (recrutement.view/edit/valider,
//   salaires en champ restreint — RecSal),
// - l'état vit côté serveur (Prisma + audit), plus de localStorage.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ENTITIES } from '@pointage/shared';
import { useUiStore } from '../store/ui.js';
import { toast } from '../store/toast.js';
import {
  useCreateRecCandidate, useRecFileUpload, useMe, useRecActivity, useRecAppEmail, useRecAppNote,
  useRecAppPresel, useRecAppPreselValidate, useRecAppReject, useRecAppRestore,
  useRecAppStage, useRecAppVivier,
  useRecInterviewers, useRecItwCreate, useRecItwPatch, useRecOfferCreate,
  useRecObBuddy, useRecObDoc, useRecObHandoff, useRecObRemindDocs,
  useRecObResource, useRecObTask, useRecOfferPatch, useRecOfferRemind,
  useRecOfferResponse, useRecOfferSend, useRecOfferSubmit, useRecOnboarding,
  useRecPipeline, useRecQueue, useRecReqStatus, useRecRequisitions,
  useRecRoles, useRecScorecard, useResubmitRecRequisition,
  type RecApplicationDTO, type RecOfferDTO, type RecOfferInput,
  type RecRequisitionDTO, type RecStageId,
} from '../api/hooks.js';
import {
  REC_ST, RecChain, RecEnt, RecSal, recContrat, recFmtDate, recOfferAction, recOfferOf,
} from '../components/recrutement/common.js';
import { RecWizard } from '../components/recrutement/Wizard.js';
import { RecQueue } from '../components/recrutement/Queue.js';
import { RecCatalogue } from '../components/recrutement/Catalogue.js';
import { RecBoard, RecCharge, RecCompare, RecList, RecReqSelector, recActiveApps } from '../components/recrutement/Pipeline.js';
import { CandidatePanel } from '../components/recrutement/CandidatePanel.js';
import { InterviewModal } from '../components/recrutement/EvalSections.js';
import { CandidateModal, EmailModal, RecModalHost, RejectModal } from '../components/recrutement/CandidateModals.js';
import { RecVivier } from '../components/recrutement/Vivier.js';
import { OfferBuilder, OfferResponseModal, RecOffres, type OfferActions } from '../components/recrutement/Offres.js';
import { RecOnboardingView, obDays, obDocsMissing, obLate, type ObActions } from '../components/recrutement/Onboarding.js';
import { RecDashboard, type RecTodo } from '../components/recrutement/Dashboard.js';
import '../styles/recrutement.css';
import '../styles/recrutement-v2.css';

type View = 'tableau' | 'pipeline' | 'offres' | 'onboarding' | 'demandes' | 'valid' | 'vivier' | 'catalogue';

function entOf(id: string) {
  return ENTITIES.find((e) => e.id === id);
}

// ── carte demande (vue Demandes) ─────────────────────────────
function ReqCard({ r, canEdit, onResubmit, onStatus, busy }: {
  r: RecRequisitionDTO;
  canEdit: boolean;
  onResubmit: (r: RecRequisitionDTO) => void;
  onStatus: (r: RecRequisitionDTO, s: 'OUVERTE' | 'GELEE' | 'ANNULEE' | 'POURVUE') => void;
  busy: boolean;
}) {
  const ent = entOf(r.entityId);
  const st = REC_ST[r.status];
  return (
    <div className="rec-q">
      <div className="rec-qh">
        <span className={`rec-stpill ${st.cls}`}>{st.lbl}</span>
        {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
      </div>
      <div className="rec-qtitle">{r.roleLabel} <span>· {r.seniority}</span></div>
      <div className="rec-qgrid">
        <div className="rec-qcell"><div className="k">Contrat</div><div className="v">{recContrat(r)}</div></div>
        <div className="rec-qcell"><div className="k">Démarrage cible</div><div className="v">{recFmtDate(r.targetStart)}</div></div>
        <div className="rec-qcell"><div className="k">Motif</div><div className="v">{r.motif}{r.replacedName ? ` ${r.replacedName}` : ''}</div></div>
        <div className="rec-qcell"><div className="k">Fourchette</div><div className="v"><RecSal band={r.band} /></div></div>
      </div>
      {r.status === 'brouillon' && r.refusalComment && (
        <div className="rec-refusnote">✕ Refusée — « {r.refusalComment} »</div>
      )}
      {(r.status === 'en_attente' || r.status === 'brouillon') && <RecChain approvals={r.approvals} />}
      {canEdit && (
        <div className="rec-actions">
          {r.status === 'brouillon' && (
            <button className="rec-go sm" disabled={busy} onClick={() => onResubmit(r)}>Re-soumettre à validation</button>
          )}
          {r.status === 'validee' && (
            <button className="rec-go sm" disabled={busy} onClick={() => onStatus(r, 'OUVERTE')}>Publier l’annonce → ouvrir</button>
          )}
          {r.status === 'ouverte' && (
            <>
              <button className="rec-ghost sm" disabled={busy} onClick={() => onStatus(r, 'GELEE')}>Geler</button>
              <button className="rec-ghost sm" disabled={busy} onClick={() => onStatus(r, 'POURVUE')}>Marquer pourvue</button>
            </>
          )}
          {r.status === 'gelee' && (
            <button className="rec-go sm" disabled={busy} onClick={() => onStatus(r, 'OUVERTE')}>Ré-ouvrir</button>
          )}
          {(r.status === 'validee' || r.status === 'ouverte' || r.status === 'gelee') && (
            <button className="rec-ghost sm danger" disabled={busy} onClick={() => onStatus(r, 'ANNULEE')}>Annuler</button>
          )}
        </div>
      )}
    </div>
  );
}

export function RecrutementPage() {
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const storeEntityId = useUiStore((s) => s.currentEntityId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizard, setWizard] = useState(false);

  const me = useMe();
  const perms = me.data?.permissions ?? [];
  const canEdit = perms.includes('recrutement.edit');
  const canValidate = perms.includes('recrutement.valider');
  const canSal = perms.includes('recrutement.salaires');
  // Éditeur de catalogue (v2) — création de rôles + templates.
  const canCat = perms.includes('recrutement.catalogue');
  // Le handoff écrit hors du module (fiche Employé) — droit dédié.
  // v2 — l'owner fait la vérification, l'offre et la fiche employé ;
  // la RH tient le dossier administratif et co-signe la décision d'essai.
  const canOffre = perms.includes('recrutement.offre');
  const canRh = perms.includes('recrutement.rh');
  const canEmp = perms.includes('employees.edit') && canOffre;
  const isBoss = me.data?.user.role === 'BOSS';
  // Manager recruteur (RECRUTEUR) : ses postes seulement (le serveur
  // filtre), sans sélecteur de société, ni demandes, offres, vivier ou
  // catalogue — juste le pipeline et l'essai de ses recrues.
  const isRecruiter = me.data?.user.role === 'RECRUTEUR';
  const entityId = isRecruiter ? 'all' : storeEntityId;

  const askedView = (['pipeline', 'offres', 'onboarding', 'demandes', 'valid', 'vivier', 'catalogue'].includes(searchParams.get('vue') ?? '')
    ? searchParams.get('vue') : 'tableau') as View;
  const view: View = isRecruiter && askedView !== 'onboarding' ? 'pipeline' : askedView;
  const setView = (v: View) => {
    const p = new URLSearchParams(searchParams);
    if (v === 'tableau') p.delete('vue'); else p.set('vue', v);
    setSearchParams(p, { replace: true });
  };

  const roles = useRecRoles();
  const reqsQuery = useRecRequisitions(entityId);
  const pipeline = useRecPipeline();
  const queue = useRecQueue(canValidate);
  const activity = useRecActivity();
  const resubmit = useResubmitRecRequisition();
  const patchStatus = useRecReqStatus();
  const createCandidate = useCreateRecCandidate();
  const uploadCv = useRecFileUpload();
  const moveStage = useRecAppStage();
  const setPresel = useRecAppPresel();
  const validatePresel = useRecAppPreselValidate();
  const rejectApp = useRecAppReject();
  const vivierApp = useRecAppVivier();
  const restoreApp = useRecAppRestore();
  const noteApp = useRecAppNote();
  const emailApp = useRecAppEmail();
  const interviewers = useRecInterviewers(canEdit);
  const itwCreate = useRecItwCreate();
  const itwPatch = useRecItwPatch();
  const scorecard = useRecScorecard();
  const offerCreate = useRecOfferCreate();
  const offerPatch = useRecOfferPatch();
  const offerSubmit = useRecOfferSubmit();
  const offerSend = useRecOfferSend();
  const offerRemind = useRecOfferRemind();
  const offerResponse = useRecOfferResponse();
  const onboarding = useRecOnboarding();
  const obTask = useRecObTask();
  const obDoc = useRecObDoc();
  const obResource = useRecObResource();
  const obBuddy = useRecObBuddy();
  const obRemind = useRecObRemindDocs();
  const obHandoff = useRecObHandoff();
  const busy = resubmit.isPending || patchStatus.isPending;
  const obBusy = obTask.isPending || obDoc.isPending || obResource.isPending
    || obBuddy.isPending || obRemind.isPending || obHandoff.isPending;
  const offerBusy = offerCreate.isPending || offerPatch.isPending || offerSubmit.isPending
    || offerSend.isPending || offerRemind.isPending || offerResponse.isPending;
  const appBusy = moveStage.isPending || setPresel.isPending || validatePresel.isPending
    || rejectApp.isPending || vivierApp.isPending || restoreApp.isPending || noteApp.isPending
    || itwCreate.isPending || itwPatch.isPending
    || scorecard.isPending || offerBusy;

  const reqs = useMemo(() => reqsQuery.data ?? [], [reqsQuery.data]);
  // Les gestionnaires du catalogue reçoivent aussi les rôles archivés
  // (restaurables) — le reste de l'app ne travaille que sur l'actif.
  const allRoles = useMemo(() => roles.data ?? [], [roles.data]);
  const activeRoles = useMemo(() => allRoles.filter((r) => r.active), [allRoles]);
  // Cloisonnement d'affichage: les candidatures suivent le filtre
  // d'entité courant via leur demande.
  const visReqIds = useMemo(() => new Set(reqs.map((r) => r.id)), [reqs]);
  const apps = useMemo(
    () => (pipeline.data ?? []).filter((a) => visReqIds.has(a.requisitionId)),
    [pipeline.data, visReqIds],
  );
  const reqOf = useMemo(() => new Map(reqs.map((r) => [r.id, r])), [reqs]);
  // Onboardings du périmètre courant (même filtre d'entité que le reste).
  const obs = useMemo(
    () => (onboarding.data ?? []).filter((o) => entityId === 'all' || o.entityId === entityId),
    [onboarding.data, entityId],
  );

  // ── état pipeline: demande sélectionnée, mode, panel, modals ──
  const [reqSel, setReqSel] = useState<string | null>(null);
  const [pmode, setPmode] = useState<'kanban' | 'liste' | 'charge' | 'compare'>('kanban');
  const [panelId, setPanelId] = useState<string | null>(null);
  const [newCand, setNewCand] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectForced, setRejectForced] = useState<string | null>(null);
  const [emailId, setEmailId] = useState<string | null>(null);
  const [itwFor, setItwFor] = useState<string | null>(null); // modal « Planifier un entretien »
  // ── état offres (phase 4) ───────────────────────────────────
  const [offerSel, setOfferSel] = useState<string | null>(null); // détail (application id)
  const [builder, setBuilder] = useState<{ appId: string; draft: RecOfferDTO | null } | null>(null);
  const [responseFor, setResponseFor] = useState<string | null>(null); // application id
  const [obFocus, setObFocus] = useState<string | null>(null); // onboarding ouvert sur l'essai

  const openReqs = useMemo(() => reqs.filter((r) => ['ouverte', 'validee'].includes(r.status)), [reqs]);
  const curReq = openReqs.find((r) => r.id === reqSel) ?? openReqs[0] ?? null;
  useEffect(() => {
    if (curReq && reqSel !== curReq.id) setReqSel(curReq.id);
  }, [curReq, reqSel]);

  const panelApp = panelId ? apps.find((a) => a.id === panelId) ?? null : null;
  const panelReq = panelApp ? reqOf.get(panelApp.requisitionId) ?? null : null;
  const panelRole = panelReq ? allRoles.find((x) => x.id === panelReq.roleId) : undefined;
  const rejectAppRow = rejectId ? apps.find((a) => a.id === rejectId) ?? null : null;
  const emailAppRow = emailId ? apps.find((a) => a.id === emailId) ?? null : null;
  const emailReq = emailAppRow ? reqOf.get(emailAppRow.requisitionId) ?? null : null;

  const openPanel = (id: string) => {
    const app = apps.find((a) => a.id === id);
    if (app && app.requisitionId !== curReq?.id && openReqs.some((r) => r.id === app.requisitionId)) {
      setReqSel(app.requisitionId);
    }
    setPanelId(id);
  };
  // Lien de notification (« appel terminé ») : ?cand=<candidature> ouvre
  // directement le panneau, puis le paramètre est retiré de l'URL.
  const candParam = searchParams.get('cand');
  useEffect(() => {
    if (!candParam || !apps.some((a) => a.id === candParam)) return;
    openPanel(candParam);
    const p = new URLSearchParams(searchParams);
    p.delete('cand');
    setSearchParams(p, { replace: true });
  }, [candParam, apps]); // eslint-disable-line react-hooks/exhaustive-deps

  const nAttente = reqs.filter((r) => r.status === 'en_attente').length;
  const nBrouillons = reqs.filter((r) => r.status === 'brouillon').length;
  const queueCount = queue.data?.length ?? 0;
  const activeApps = useMemo(
    () => apps.filter((a) => !['REJETE', 'VIVIER', 'RECRUTE'].includes(a.stage)),
    [apps],
  );
  const nATraiter = canEdit ? activeApps.filter((a) => a.waitOn).length : 0;
  const nVivier = apps.filter((a) => a.stage === 'VIVIER').length;
  // Offres qui attendent un geste de l'équipe (brouillon, à envoyer,
  // deadline dépassée, contre-proposition) — badge de l'onglet.
  const nOffresTodo = useMemo(
    () => apps.filter((a) => {
      const o = recOfferOf(a);
      return o != null && recOfferAction(o) != null;
    }).length,
    [apps],
  );

  const mutErr = (fallback: string) => (err: unknown) =>
    toast.danger((err as { body?: { message?: string } }).body?.message ?? fallback);

  const onResubmit = (r: RecRequisitionDTO) => {
    resubmit.mutate({ id: r.id }, {
      onSuccess: () => toast.success(`${r.roleLabel} — demande re-soumise, la chaîne de validation repart au directeur.`),
      onError: mutErr('Échec de la re-soumission.'),
    });
  };
  const onStatus = (r: RecRequisitionDTO, s: 'OUVERTE' | 'GELEE' | 'ANNULEE' | 'POURVUE') => {
    patchStatus.mutate({ id: r.id, status: s }, {
      onSuccess: () => toast.success(
        s === 'OUVERTE' ? `${r.roleLabel} — annonce publiée, le poste est ouvert.`
          : s === 'GELEE' ? `${r.roleLabel} — poste gelé.`
            : s === 'POURVUE' ? `${r.roleLabel} — poste pourvu 🎉`
              : `${r.roleLabel} — demande annulée.`,
      ),
      onError: mutErr('Échec du changement de statut.'),
    });
  };

  // ── actions pipeline ────────────────────────────────────────
  const nameOf = (a: RecApplicationDTO) => `${a.candidate.firstName} ${a.candidate.lastName}`;
  const onMove = (id: string, stage: RecStageId, how = 'glisser-déposer') => {
    const app = apps.find((a) => a.id === id);
    if (!app || app.stage === stage) return;
    if (stage === 'OFFRE') {
      toast.info('L’offre passe par le constructeur — ouvrez la fiche du finaliste ou la vue Offres.');
      return;
    }
    if (stage === 'RECRUTE') {
      toast.info('« Recruté » arrive avec l’acceptation de l’offre (vue Offres → Enregistrer la réponse).');
      return;
    }
    moveStage.mutate({ id, stage, how }, {
      onSuccess: (out) => toast.success(`${nameOf(app)} déplacé·e vers « ${out.stage === 'APPEL' ? 'Appel de qualif' : out.stage.toLowerCase()} »`),
      onError: mutErr('Déplacement impossible.'),
    });
  };
  const onPreselSet = (id: string, critId: string, value: boolean | null) => {
    setPresel.mutate({ id, critId, value }, { onError: mutErr('Échec de la mise à jour du critère.') });
  };
  const onPreselValidate = (id: string) => {
    const app = apps.find((a) => a.id === id);
    if (!app) return;
    const role = panelRole;
    const failed = ((role?.templates.presel ?? []) as Array<{ id: string; elim?: boolean }>).some((c) => c.elim !== false && app.presel[c.id] === false);
    if (failed) {
      setRejectId(id);
      return;
    }
    validatePresel.mutate({ id }, {
      onSuccess: () => toast.success(`${nameOf(app)} — présélection validée, passage à l’appel de qualification.`),
      onError: mutErr('Validation impossible.'),
    });
  };
  const onReject = (id: string, motif: string, note: string) => {
    const app = apps.find((a) => a.id === id);
    rejectApp.mutate({ id, motif, ...(note ? { note } : {}) }, {
      onSuccess: () => {
        setRejectId(null);
        toast.success(`Candidature rejetée (${motif})${app ? ` — pensez à l’email « Réponse négative » pour ${app.candidate.firstName}.` : '.'}`);
      },
      onError: mutErr('Rejet impossible.'),
    });
  };
  const onVivier = (id: string) => {
    const app = apps.find((a) => a.id === id);
    vivierApp.mutate({ id }, {
      onSuccess: () => toast.success(`${app ? nameOf(app) : 'Candidat'} ajouté·e au vivier — retrouvable par rôle et tags.`),
      onError: mutErr('Mise au vivier impossible.'),
    });
  };
  const onRestore = (id: string) => {
    const app = apps.find((a) => a.id === id);
    restoreApp.mutate({ id }, {
      onSuccess: () => toast.success(`${app ? nameOf(app) : 'Candidat'} repêché·e — de retour en « Nouvelle ».`),
      onError: mutErr('Repêchage impossible.'),
    });
  };
  const onNote = (id: string, text: string) => {
    noteApp.mutate({ id, text }, { onError: mutErr('Note non enregistrée.') });
  };
  const onSendEmail = (id: string, templateId: string) => {
    const app = apps.find((a) => a.id === id);
    emailApp.mutate({ id, templateId }, {
      onSuccess: () => {
        setEmailId(null);
        toast.success(`Email envoyé à ${app ? app.candidate.firstName : 'ce candidat'} — tracé au journal.`);
      },
      onError: (err) => {
        setEmailId(null);
        mutErr('Envoi impossible — vérifiez votre connexion Gmail (reconnectez-vous).')(err);
      },
    });
  };
  // ── actions évaluation (phase 3) ────────────────────────────
  const onItwCreate = (id: string, input: Record<string, unknown>) => {
    const app = apps.find((a) => a.id === id);
    itwCreate.mutate({ id, ...input }, {
      onSuccess: () => {
        setItwFor(null);
        toast.success(`Entretien planifié${app ? ` — pensez à l’email « Convocation » pour ${app.candidate.firstName}.` : '.'}`);
      },
      onError: mutErr('Planification impossible.'),
    });
  };
  const onItwStatus = (id: string, status: 'REALISE' | 'ANNULE' | 'NO_SHOW') => {
    itwPatch.mutate({ id, status }, {
      onSuccess: () => toast.success(
        status === 'REALISE' ? 'Entretien réalisé — les scorecards du jury sont ouvertes.'
          : status === 'ANNULE' ? 'Entretien annulé.' : 'No-show enregistré — un signal sur le process.',
      ),
      onError: mutErr('Mise à jour impossible.'),
    });
  };
  const onScorecard = (itwId: string, scores: Record<string, number>, verdict: 'oui' | 'reserve' | 'non', comment: string, evidence: Record<string, string>) => {
    scorecard.mutate({ id: itwId, scores, evidence, verdict, comment }, {
      onSuccess: () => toast.success('Scorecard soumise ✓ — les évaluations des autres sont maintenant révélées.'),
      onError: mutErr('Soumission impossible.'),
    });
  };
  // ── actions offres (phase 4) ────────────────────────────────
  const appOfOffer = (offerId: string) =>
    apps.find((a) => a.offers.some((o) => o.id === offerId)) ?? null;
  const offerAct: OfferActions = {
    onBuild: (appId, draft) => setBuilder({ appId, draft }),
    onSubmit: (offerId) => {
      offerSubmit.mutate({ id: offerId }, {
        onSuccess: (app) => toast.success(`Offre v${recOfferOf(app)?.version} soumise — chaîne directeur → owner armée, rien ne part avant validation.`),
        onError: mutErr('Soumission impossible.'),
      });
    },
    onSend: (offerId) => {
      offerSend.mutate({ id: offerId }, {
        onSuccess: (app) => toast.success(`Offre envoyée à ${app.candidate.firstName} — PDF joint, deadline de réponse armée.`),
        onError: mutErr('Envoi impossible — vérifiez votre connexion Gmail (reconnectez-vous).'),
      });
    },
    onRemind: (offerId) => {
      offerRemind.mutate({ id: offerId }, {
        onSuccess: () => toast.success('Relance envoyée — PDF re-joint, tracée sur la version.'),
        onError: mutErr('Relance impossible.'),
      });
    },
    onResponse: (offerId) => {
      const a = appOfOffer(offerId);
      if (a) setResponseFor(a.id);
    },
  };
  const onOfferSave = (input: RecOfferInput, submit: boolean) => {
    if (!builder) return;
    const after = (app: RecApplicationDTO) => {
      setBuilder(null);
      const o = recOfferOf(app);
      if (submit && o) offerAct.onSubmit(o.id);
      else toast.success(`Brouillon v${o?.version ?? ''} enregistré — soumettez-le quand il est prêt.`);
    };
    if (builder.draft) {
      offerPatch.mutate({ id: builder.draft.id, ...input }, { onSuccess: after, onError: mutErr('Enregistrement impossible.') });
    } else {
      offerCreate.mutate({ id: builder.appId, ...input }, { onSuccess: after, onError: mutErr('Création impossible.') });
    }
  };
  const onOfferResponse = (resp: 'acceptee' | 'refusee' | 'contre', counter: string) => {
    const app = responseFor ? apps.find((a) => a.id === responseFor) : null;
    const offer = app ? recOfferOf(app) : null;
    if (!app || !offer) return;
    offerResponse.mutate({ id: offer.id, response: resp, ...(counter ? { counter } : {}) }, {
      onSuccess: () => {
        setResponseFor(null);
        if (resp === 'acceptee') toast.success(`🎉 ${nameOf(app)} a accepté — onboarding et période d’essai créés (objectifs 90 j, points J+15 → décision).`);
        else if (resp === 'contre') toast.success('Contre-proposition enregistrée — construisez la version suivante, elle re-entre la chaîne.');
        else toast.success(`Refus enregistré — candidature clôturée « Désistement », réponse négative à envoyer à ${app.candidate.firstName}.`);
      },
      onError: mutErr('Enregistrement impossible.'),
    });
  };
  // ── actions onboarding (phase 5) ────────────────────────────
  const obName = (obId: string) => {
    const ob = obs.find((o) => o.id === obId);
    return ob ? `${ob.candidate.firstName} ${ob.candidate.lastName}` : 'la recrue';
  };
  const obAct: ObActions = {
    onTask: (obId, hz, taskId, done) => {
      obTask.mutate({ id: obId, hz, taskId, done }, { onError: mutErr('Tâche non enregistrée.') });
    },
    onDoc: (obId, docId, st) => {
      obDoc.mutate({ id: obId, docId, st }, {
        onSuccess: (ob) => {
          if (st === 'ok' && ob.dossier.every((x) => x.st === 'ok')) {
            toast.success(`🗂 Dossier admin de ${ob.candidate.firstName} complet — tout est validé.`);
          }
        },
        onError: mutErr('Pièce non mise à jour.'),
      });
    },
    onResource: (obId, index) => {
      obResource.mutate({ id: obId, index }, { onError: mutErr('Partage non enregistré.') });
    },
    onBuddy: (obId, userId) => {
      obBuddy.mutate({ id: obId, userId }, {
        onSuccess: (ob) => toast.success(ob.buddy ? `🤝 ${ob.buddy.name} accompagnera ${ob.candidate.firstName} — semaine 1.` : 'Buddy retiré.'),
        onError: mutErr('Buddy non enregistré.'),
      });
    },
    onRemindDocs: (obId) => {
      obRemind.mutate({ id: obId }, {
        onSuccess: () => toast.success(`Relance envoyée à ${obName(obId)} — pièces manquantes listées, tracée au journal.`),
        onError: mutErr('Relance impossible — vérifiez votre connexion Gmail (reconnectez-vous).'),
      });
    },
    onHandoff: (obId) => {
      obHandoff.mutate({ id: obId }, {
        onSuccess: (ob) => toast.success(
          `🎉 Fiche ${ob.employee?.code} créée pour ${ob.candidate.firstName} — essai ouvert, demande pourvue, autres candidatures clôturées (réponses négatives en brouillon). Pensez au rattachement organigramme.`,
        ),
        onError: mutErr('Handoff impossible.'),
      });
    },
  };

  const onCreateCandidate = (input: Parameters<typeof createCandidate.mutate>[0], cv: File | null) => {
    createCandidate.mutate(input, {
      onSuccess: (app) => {
        setNewCand(false);
        setReqSel(app.requisitionId);
        toast.success(`${nameOf(app)} — candidature créée, consentement horodaté.`);
        // Le CV part juste après la création (la pièce s'accroche à la candidature).
        if (cv) uploadCv.mutate({ appId: app.id, file: cv, kind: 'cv' }, {
          onError: () => toast.danger(`CV de ${nameOf(app)} non téléversé — ajoutez-le depuis sa fiche (Documents).`),
        });
        if (view !== 'pipeline') setView('pipeline');
      },
      onError: mutErr('Création impossible.'),
    });
  };

  // « À traiter » du tableau de bord — personnalisé selon les droits.
  const todos = useMemo(() => {
    const t: RecTodo[] = [];
    // Mes scorecards en retard — le meilleur indicateur d'adoption
    // (spec §10); visible même pour un juré sans droit d'édition.
    const meIdVal = me.data?.user.id;
    if (meIdVal) {
      for (const a of activeApps) {
        for (const itw of a.interviews) {
          if (itw.status === 'REALISE' && itw.interviewers.some((j) => j.id === meIdVal) && !itw.myScorecard) {
            t.push({
              ic: '📊',
              b: `Scorecard à soumettre — ${nameOf(a)}`,
              s: `${itw.type} · les évaluations des autres se révèlent après la vôtre`,
              go: () => { setView('pipeline'); openPanel(a.id); },
            });
          }
        }
      }
    }
    if (canValidate) {
      for (const it of queue.data ?? []) {
        if (!it.canDecide) continue;
        if (it.kind === 'offer') {
          const ent = entOf(it.entityId);
          t.push({
            ic: '✉️',
            b: `Offre v${it.offer.version} à valider — ${it.candidate.firstName} ${it.candidate.lastName}`,
            s: `${it.roleLabel}${ent ? ` · ${ent.name}` : ''} — jury, montant et fourchette sur un écran`,
            go: () => setView('valid'),
          });
        } else {
          const ent = entOf(it.requisition.entityId);
          t.push({
            ic: '🪪',
            b: `Demande à valider — ${it.requisition.roleLabel}${ent ? ` (${ent.name})` : ''}`,
            s: 'Tout le contexte de décision sur un écran',
            go: () => setView('valid'),
          });
        }
      }
    }
    // Offres qui attendent un geste (constructeur, envoi, relance, v+1).
    if (canEdit && canSal) {
      for (const a of apps) {
        const o = recOfferOf(a);
        const action = o ? recOfferAction(o) : null;
        if (!o || !action) continue;
        t.push({
          ic: '✉️',
          b: `${action.split(' — ')[0]} — ${nameOf(a)}`,
          s: action.split(' — ')[1] ?? `Offre v${o.version}`,
          go: () => { setView('offres'); setOfferSel(a.id); },
        });
      }
    }
    if (canEdit) {
      for (const a of activeApps.filter((x) => x.waitOn)) {
        const r = reqOf.get(a.requisitionId);
        t.push({
          ic: a.stage === 'NOUVELLE' || a.stage === 'PRESEL' ? '📋' : '⏳',
          b: `${a.stage === 'NOUVELLE' || a.stage === 'PRESEL' ? 'Présélection à faire' : 'Candidature à traiter'} — ${nameOf(a)}`,
          s: r ? `${r.roleLabel} · ${entOf(r.entityId)?.name ?? ''}` : '',
          go: () => { setView('pipeline'); openPanel(a.id); },
        });
      }
      for (const r of reqs.filter((x) => x.status === 'brouillon')) {
        t.push({ ic: '↩️', b: `Refusée — ${r.roleLabel}`, s: r.refusalComment ? `« ${r.refusalComment} » — ajustez puis re-soumettez` : 'À ajuster puis re-soumettre', go: () => setView('demandes') });
      }
      for (const r of reqs.filter((x) => x.status === 'validee')) {
        t.push({ ic: '📢', b: `Annonce à publier — ${r.roleLabel}`, s: 'Demande validée par la chaîne — ouvrir le poste', go: () => setView('demandes') });
      }
    }
    if (canRh) {
      // Onboarding: dossiers incomplets et plans en retard (phase 5) — RH.
      for (const ob of obs) {
        const manq = ob.dossier.filter((x) => x.st === 'manq').length;
        const recu = ob.dossier.filter((x) => x.st === 'recu').length;
        const late = obLate(ob);
        if (manq > 0 || recu > 0) {
          t.push({
            ic: '🗂',
            b: `Dossier admin de ${ob.candidate.firstName} — ${manq > 0 ? `${manq} document${manq > 1 ? 's' : ''} manquant${manq > 1 ? 's' : ''}` : `${recu} pièce${recu > 1 ? 's' : ''} à valider`}`,
            s: `${ob.roleLabel} · ${obDays(ob) < 0 ? `démarrage J−${-obDays(ob)}` : 'en poste'} — relance email en un clic`,
            go: () => setView('onboarding'),
          });
        }
        if (late > 0) {
          t.push({
            ic: '⏰',
            b: `Plan d’intégration en retard — ${ob.candidate.firstName} ${ob.candidate.lastName}`,
            s: `${late} tâche${late > 1 ? 's' : ''} au-delà de l’horizon prévu`,
            go: () => setView('onboarding'),
          });
        }
        if (!ob.employee && obDays(ob) >= 0) {
          t.push({
            ic: '🪪',
            b: `Fiche employé à créer — ${ob.candidate.firstName} ${ob.candidate.lastName}`,
            s: 'En poste sans fiche — le handoff initialise la période d’essai',
            go: () => setView('onboarding'),
          });
        }
      }
    }
    // ── v2 — preuves avant l'embauche, essai, réponses négatives ──
    const myName = me.data?.user.name;
    for (const a of apps) {
      if (a.negReply === 'open' && a.closedBy === myName) {
        t.push({ ic: '✉️', b: `Réponse négative à envoyer — ${nameOf(a)}`, s: `${a.rejectMotif ?? 'Clôturée'} · rien n’est parti au candidat`, go: () => setEmailId(a.id) });
      }
    }
    if (canEdit) {
      for (const a of activeApps) {
        if (a.test?.status === 'RENDU') t.push({ ic: '🧪', b: `Cas pratique rendu à noter — ${nameOf(a)}`, s: 'Rubrique par dimension · preuve pour 1, 2 et 5', go: () => { setView('pipeline'); openPanel(a.id); } });
        if (a.test?.status === 'A_ENVOYER' && a.stage === 'TEST') t.push({ ic: '🧪', b: `Cas pratique à envoyer — ${nameOf(a)}`, s: 'Transmettez le brief puis marquez l’envoi', go: () => { setView('pipeline'); openPanel(a.id); } });
      }
    }
    if (canOffre) {
      for (const a of activeApps.filter((x) => x.stage === 'FINALISTE')) {
        if (!a.gates.verif.ok) t.push({ ic: '🛡', b: `Vérification à mener — ${nameOf(a)}`, s: `${a.gates.verif.n}/2 références (${a.gates.verif.mgr} manager) · certificats · intégrité — bloque l’offre`, go: () => { setView('pipeline'); openPanel(a.id); } });
        if (isBoss && a.gates.bar?.state === 'ko' && !a.gates.overridden) t.push({ ic: '⚖️', b: `Sous la barre — ${nameOf(a)}`, s: `${a.gates.bar.fails.join(' · ')} · dérogation possible, motivée`, go: () => { setView('pipeline'); openPanel(a.id); } });
      }
    }
    const meIdV = me.data?.user.id;
    for (const ob of obs) {
      const day = Math.round((Date.now() - new Date(`${ob.start}T00:00:00`).getTime()) / 864e5);
      const nm = `${ob.candidate.firstName} ${ob.candidate.lastName}`;
      for (const cp of ob.checkpoints) {
        if (cp.done || day < cp.day - 2) continue;
        const late = day > cp.day;
        if (cp.type === 'final') {
          const signs = cp.data.signs ?? {};
          const signed = Object.values(signs).some((x) => x.byId === meIdV);
          const mySlot = (meIdV === ob.hiringManagerId && !signs.mgr) || (canRh && !signs.rh && !isBoss) || (isBoss && !signs.boss);
          if (!signed && mySlot) t.push({ ic: '⚖️', b: `Décision d’essai à signer — ${nm}`, s: `${late ? `en retard de ${day - cp.day} j` : `J+${cp.day}`} · manager + RH + owner doivent concorder`, go: () => { setObFocus(ob.id); setView('onboarding'); } });
          continue;
        }
        const mine = meIdV === ob.hiringManagerId || (isBoss && late && day - cp.day > 3);
        if (mine) t.push({ ic: late ? '⏰' : '🗓', b: `${cp.lbl} — ${nm}`, s: late ? `en retard de ${day - cp.day} j` : day === cp.day ? 'aujourd’hui' : `dans ${cp.day - day} j`, go: () => { setObFocus(ob.id); setView('onboarding'); } });
      }
    }
    return t;
  }, [queue.data, reqs, apps, activeApps, obs, canValidate, canEdit, canSal, canOffre, canRh, isBoss, me.data?.user.id, me.data?.user.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Badge Onboarding: recrues avec un dossier incomplet ou du retard.
  const nObTodo = canEdit ? obs.filter((o) => obDocsMissing(o) > 0 || obLate(o) > 0).length : 0;

  const VIEWS: Array<[View, string, number?]> = [
    ['tableau', 'Tableau de bord'],
    ['pipeline', 'Pipeline', nATraiter],
    ['offres', 'Offres', canSal ? nOffresTodo : 0],
    ['onboarding', 'Onboarding & essai', nObTodo],
    ['demandes', 'Demandes', nAttente + nBrouillons],
    ...(canValidate ? [['valid', 'Validations', queueCount] as [View, string, number]] : []),
    ['vivier', 'Vivier', 0],
    ['catalogue', 'Catalogue rôles'],
  ].filter(([k]) => !isRecruiter || k === 'pipeline' || k === 'onboarding') as Array<[View, string, number?]>;

  return (
    <div className={`recapp${themeDark ? ' theme-dark' : ''}`}>
      <div className="rec-scrollpage">
        <div className="rec-wrap">
          <div className="rec-eyebrow">Talent · Recrutement & onboarding</div>
          <h1 className="rec-title">Recrutement <em>& onboarding</em></h1>
          <p className="rec-sub">
            {isRecruiter
              ? 'Les postes qui vous sont confiés : présélection, appel, cas pratique, entretiens, puis les points de la période d’essai de votre recrue.'
              : 'Demandes validées avant toute annonce, fourchettes restreintes RH & direction — le recrutement ne sort jamais de la plateforme.'}
          </p>
          <div className="rec-ctrl">
            <div className="rec-seg">
              {VIEWS.map(([k, l, b]) => (
                <button key={k} className={view === k ? 'on' : ''} onClick={() => setView(k)}>
                  {l}{(b ?? 0) > 0 && <span className="bdg">{b}</span>}
                </button>
              ))}
            </div>
            <div className="rec-spacer"></div>
            {canEdit && view === 'pipeline' && openReqs.length > 0 && (
              <button className="rec-ghost" onClick={() => setNewCand(true)}>＋ Nouveau candidat</button>
            )}
            {canEdit && !isRecruiter && <button className="rec-go" onClick={() => setWizard(true)}>＋ Nouvelle demande</button>}
          </div>

          <div className="rec-view" key={view}>
            {view === 'tableau' && (
              <RecDashboard
                meName={me.data?.user.name ?? ''}
                reqs={reqs} apps={apps} activeApps={activeApps}
                queue={queue.data ?? []} obs={obs} roles={activeRoles}
                activity={activity.data ?? []} todos={todos}
                canSal={canSal} canValidate={canValidate} canEdit={canEdit}
                onJump={(v) => setView(v as View)}
                onOpenApp={(id) => { setView('pipeline'); openPanel(id); }}
                onNew={() => setWizard(true)}
              />
            )}

            {view === 'pipeline' && (
              <div>
                <RecReqSelector reqs={reqs} apps={apps} sel={curReq?.id ?? null} onSel={setReqSel} />
                {curReq && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 12px', flexWrap: 'wrap' }}>
                      <div className="rec-seg">
                        {([['kanban', 'Kanban'], ['liste', 'Liste'], ['charge', 'Balle dans le camp'], ['compare', 'Face à la barre']] as const).map(([k, l]) => (
                          <button key={k} className={pmode === k ? 'on' : ''} onClick={() => setPmode(k)}>{l}</button>
                        ))}
                      </div>
                      <span className="rec-hint">
                        {pmode === 'kanban' ? 'Par étape — glissez pour avancer' : pmode === 'liste' ? 'Vue dense triable — pour passer en revue' : pmode === 'charge' ? 'Par responsabilité — à qui la balle, maintenant' : 'Chacun comparé à la barre, jamais aux autres — radar des 4 dimensions'}
                      </span>
                    </div>
                    {pmode === 'kanban' && <RecBoard apps={apps} reqId={curReq.id} canEdit={canEdit} onOpen={openPanel} onMove={onMove} />}
                    {pmode === 'liste' && <RecList apps={apps} reqId={curReq.id} canEdit={canEdit} onOpen={openPanel} />}
                    {pmode === 'charge' && <RecCharge apps={apps} reqId={curReq.id} onOpen={openPanel} />}
                    {pmode === 'compare' && (
                      <RecCompare
                        apps={apps} reqId={curReq.id}
                        role={allRoles.find((x) => x.id === curReq.roleId)}
                        onOpen={openPanel}
                      />
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} className="rec-hint">
                      <span>{pmode === 'kanban' ? 'Glissez une carte pour avancer · ' : ''}Sorties possibles à chaque étape : Rejeté (motif obligatoire) · Vivier</span>
                      <span style={{ marginLeft: 'auto' }}>
                        {apps.filter((a) => a.stage === 'REJETE').length} rejeté·e·s · {nVivier} au vivier
                      </span>
                    </div>
                  </>
                )}
                {!curReq && !recActiveApps(apps, '').length && (
                  <div className="rec-emptybox big" style={{ marginTop: 4 }}>
                    📭 Aucun poste ouvert — validez et publiez une demande pour démarrer un pipeline.
                  </div>
                )}
              </div>
            )}

            {view === 'offres' && (
              <RecOffres
                apps={apps} reqs={reqs} roles={allRoles} dark={themeDark}
                canEdit={canEdit} canSal={canSal} busy={appBusy}
                sel={offerSel} onSel={setOfferSel} act={offerAct}
                onOpenCandidate={openPanel}
              />
            )}

            {view === 'onboarding' && (
              <RecOnboardingView
                key={obFocus ?? 'all'} focusId={obFocus}
                obs={obs} dark={themeDark} canEdit={canRh} canEmp={canEmp}
                busy={obBusy} interviewers={interviewers.data ?? []} act={obAct}
              />
            )}

            {view === 'demandes' && (
              <div className="r13-grid">
                {reqs.map((r) => (
                  <ReqCard key={r.id} r={r} canEdit={canEdit} busy={busy} onResubmit={onResubmit} onStatus={onStatus} />
                ))}
                {!reqs.length && <div className="rec-emptybox big">📭 Aucune demande dans ce périmètre — « Nouvelle demande » pour ouvrir la première.</div>}
              </div>
            )}

            {view === 'valid' && canValidate && <RecQueue items={queue.data ?? []} apps={apps} />}

            {view === 'vivier' && (
              <RecVivier apps={apps} reqs={reqs} canEdit={canEdit} busy={appBusy} onOpen={openPanel} onRestore={onRestore} />
            )}

            {view === 'catalogue' && (
              <RecCatalogue roles={allRoles} reqs={reqs} canSal={canSal} canCat={canCat} dark={themeDark} />
            )}
          </div>
        </div>
      </div>
      {wizard && <RecWizard roles={activeRoles} onClose={() => setWizard(false)} />}
      {newCand && (
        <RecModalHost dark={themeDark}>
          <CandidateModal
            reqs={reqs} defaultReqId={curReq?.id ?? null} busy={createCandidate.isPending || uploadCv.isPending}
            onClose={() => setNewCand(false)} onSubmit={onCreateCandidate}
          />
        </RecModalHost>
      )}
      {panelApp && panelReq && (
        <CandidatePanel
          app={panelApp} req={panelReq} role={panelRole} dark={themeDark}
          canEdit={canEdit} canSal={canSal} meId={me.data?.user.id ?? ''}
          busy={appBusy}
          onClose={() => setPanelId(null)}
          onAdvance={(stage) => onMove(panelApp.id, stage, 'avancée manuelle')}
          onPresel={(critId, value) => onPreselSet(panelApp.id, critId, value)}
          onPreselValidate={() => onPreselValidate(panelApp.id)}
          onReject={(forced) => { setRejectForced(forced ?? null); setRejectId(panelApp.id); }}
          onVivier={() => onVivier(panelApp.id)}
          onRestore={() => onRestore(panelApp.id)}
          onEmail={() => setEmailId(panelApp.id)}
          onNote={(text) => onNote(panelApp.id, text)}
          onPlanItw={() => setItwFor(panelApp.id)}
          onItwStatus={onItwStatus}
          onScorecard={onScorecard}
          offerAct={offerAct}
        />
      )}
      {builder && (() => {
        const app = apps.find((a) => a.id === builder.appId);
        const r = app ? reqOf.get(app.requisitionId) : null;
        return app && r ? (
          <RecModalHost dark={themeDark}>
            <OfferBuilder
              app={app} req={r} role={allRoles.find((x) => x.id === r.roleId)}
              draft={builder.draft} busy={offerBusy}
              onClose={() => setBuilder(null)} onSave={onOfferSave}
            />
          </RecModalHost>
        ) : null;
      })()}
      {responseFor && (() => {
        const app = apps.find((a) => a.id === responseFor);
        return app && recOfferOf(app) ? (
          <RecModalHost dark={themeDark}>
            <OfferResponseModal
              app={app} busy={offerBusy}
              onClose={() => setResponseFor(null)} onConfirm={onOfferResponse}
            />
          </RecModalHost>
        ) : null;
      })()}
      {itwFor && (() => {
        const app = apps.find((a) => a.id === itwFor);
        const r = app ? reqOf.get(app.requisitionId) : null;
        const role = r ? allRoles.find((x) => x.id === r.roleId) : undefined;
        return app ? (
          <RecModalHost dark={themeDark}>
            <InterviewModal
              app={app} role={role} interviewers={interviewers.data ?? []}
              meId={me.data?.user.id ?? ''} busy={itwCreate.isPending}
              onClose={() => setItwFor(null)}
              onSubmit={(input) => onItwCreate(app.id, input)}
            />
          </RecModalHost>
        ) : null;
      })()}
      {rejectAppRow && (
        <RecModalHost dark={themeDark}>
          <RejectModal
            app={rejectAppRow} busy={rejectApp.isPending} forced={rejectForced}
            onClose={() => { setRejectId(null); setRejectForced(null); }}
            onConfirm={(motif, note) => onReject(rejectAppRow.id, motif, note)}
          />
        </RecModalHost>
      )}
      {emailAppRow && emailReq && (
        <RecModalHost dark={themeDark}>
          <EmailModal
            app={emailAppRow} req={emailReq}
            meName={me.data?.user.name ?? ''} meEmail={me.data?.user.email ?? ''}
            busy={emailApp.isPending}
            onClose={() => setEmailId(null)}
            onSend={(templateId) => onSendEmail(emailAppRow.id, templateId)}
          />
        </RecModalHost>
      )}
    </div>
  );
}
